import type { Theme, Components } from '@mui/material/styles';

import { varAlpha } from 'minimal-shared/utils';

import { inputBaseClasses } from '@mui/material/InputBase';
import { filledInputClasses } from '@mui/material/FilledInput';
import { outlinedInputClasses } from '@mui/material/OutlinedInput';

const borderRadiusValue = 12;
const outlinedBorderWidth = 2;

const placeholderStyle = {
  opacity: 1,
  color: '#A2A1A1',
  fontSize: '16px',
};

const labelStyle = {
  color: '#A2A1A1',
  fontSize: '16px',
  fontWeight: 400,
};

const MuiInputLabel: Components<Theme>['MuiInputLabel'] = {
  styleOverrides: {
    root: {
      color: labelStyle.color,
      fontSize: labelStyle.fontSize,
      fontWeight: labelStyle.fontWeight,
      // Ensure the focused label uses the same style
      '&.Mui-focused': {
        color: labelStyle.color,
        fontSize: labelStyle.fontSize,
        fontWeight: labelStyle.fontWeight,
      },
      // Ensure the shrink/floating label uses the same style
      '&.MuiInputLabel-shrink': {
        color: labelStyle.color,
        fontSize: labelStyle.fontSize,
        fontWeight: labelStyle.fontWeight,
      },
      '&.Mui-error': {
        color: labelStyle.color,
      },
    },
  },
};

const MuiInputBase: Components<Theme>['MuiInputBase'] = {
  /** **************************************
   * STYLE
   *************************************** */
  styleOverrides: {
    root: ({ theme }) => ({
      borderRadius: borderRadiusValue,
      [`&.${inputBaseClasses.disabled}`]: { '& svg': { color: theme.vars.palette.text.disabled } },
      [`& .${inputBaseClasses.input}:focus`]: { borderRadius: 'inherit' },
    }),
    input: ({ theme }) => ({
      fontSize: theme.typography.pxToRem(15),
      [theme.breakpoints.down('sm')]: {
        // This will prevent zoom in Safari min font size ~ 16px
        fontSize: theme.typography.pxToRem(16),
      },
      backgroundColor: '#fff',
      '&::placeholder': placeholderStyle,
    }),
  },
};

const MuiInput: Components<Theme>['MuiInput'] = {
  /** **************************************
   * STYLE
   *************************************** */
  styleOverrides: {
    underline: ({ theme }) => ({
      '&::before': { borderBottomColor: varAlpha(theme.vars.palette.grey['500Channel'], 0.32) },
      '&::after': { borderBottomColor: theme.vars.palette.text.primary },
    }),
    root: {
      backgroundColor: '#fff',
      borderRadius: borderRadiusValue,
      border: `1px solid #bdbdbd`, // fallback for grey[400]
    },
    input: {
      backgroundColor: '#fff',
      '&::placeholder': placeholderStyle,
    },
  },
};

const MuiOutlinedInput: Components<Theme>['MuiOutlinedInput'] = {
  /** **************************************
   * STYLE
   *************************************** */
  styleOverrides: {
    root: ({ theme }) => ({
      backgroundColor: '#fff',
      borderRadius: borderRadiusValue,
      [`& .${outlinedInputClasses.notchedOutline}`]: {
        borderColor: theme.vars.palette.grey[400],
        borderWidth: outlinedBorderWidth,
        borderRadius: borderRadiusValue,
      },
      '&:hover': {
        [`& .${outlinedInputClasses.notchedOutline}`]: {
          borderColor: theme.vars.palette.grey[400],
          borderWidth: outlinedBorderWidth,
        },
      },
      [`&.${outlinedInputClasses.focused}`]: {
        [`& .${outlinedInputClasses.notchedOutline}`]: {
          borderColor: theme.vars.palette.text.primary,
          borderWidth: outlinedBorderWidth,
        },
      },
      [`&.${outlinedInputClasses.error}`]: {
        [`& .${outlinedInputClasses.notchedOutline}`]: {
          borderColor: theme.vars.palette.error.main,
          borderWidth: outlinedBorderWidth,
        },
      },
      [`&.${outlinedInputClasses.disabled}`]: {
        [`& .${outlinedInputClasses.notchedOutline}`]: {
          borderColor: theme.vars.palette.action.disabledBackground,
          borderWidth: outlinedBorderWidth,
        },
      },
    }),
    notchedOutline: ({ theme }) => ({
      borderColor: theme.vars.palette.grey[400],
      borderWidth: outlinedBorderWidth,
      borderRadius: borderRadiusValue,
      transition: theme.transitions.create(['border-color'], {
        duration: theme.transitions.duration.shortest,
      }),
    }),
    input: {
      backgroundColor: '#fff',
      borderRadius: borderRadiusValue,
      '&::placeholder': placeholderStyle,
    },
  },
};

const MuiFilledInput: Components<Theme>['MuiFilledInput'] = {
  /** **************************************
   * DEFAULT PROPS
   *************************************** */
  defaultProps: { disableUnderline: true },

  /** **************************************
   * STYLE
   *************************************** */
  styleOverrides: {
    root: ({ theme }) => ({
      borderRadius: borderRadiusValue,
      backgroundColor: '#fff',
      border: `1px solid ${theme.vars.palette.grey[400]}`,
      '&:hover': { backgroundColor: '#fff' },
      [`&.${filledInputClasses.focused}`]: {
        backgroundColor: '#fff',
      },
      [`&.${filledInputClasses.error}`]: {
        backgroundColor: varAlpha(theme.vars.palette.error.mainChannel, 0.08),
        [`&.${filledInputClasses.focused}`]: {
          backgroundColor: varAlpha(theme.vars.palette.error.mainChannel, 0.16),
        },
      },
      [`&.${filledInputClasses.disabled}`]: {
        backgroundColor: theme.vars.palette.action.disabledBackground,
      },
    }),
    input: ({ theme }) => ({
      fontSize: theme.typography.pxToRem(15),
      [theme.breakpoints.down('sm')]: {
        fontSize: theme.typography.pxToRem(16),
      },
      backgroundColor: '#fff',
      '&::placeholder': placeholderStyle,
    }),
  },
};

const MuiTextField: Components<Theme>['MuiTextField'] = {
  /** **************************************
   * DEFAULT PROPS
   *************************************** */
  defaultProps: { variant: 'outlined' },

  /** **************************************
   * STYLE
   *************************************** */
  styleOverrides: {},
  variants: [
    {
      props: { variant: 'standard' },
      style: ({ theme }) => ({
        '& .MuiInput-root': {
          backgroundColor: 'transparent',
          border: 'none',
          borderBottom: `2px solid ${theme.vars.palette.grey[400]}`,
          borderRadius: '0',
          padding: '8px 0',
          transition: 'border-color 0.2s ease-in-out',
          '&:hover': {
            '&:before': {
              borderBottom: `2px solid ${theme.vars.palette.grey[400]}`,
            },
            '&:after': {
              borderBottom: 'none',
            },
          },
          '&:hover:not(.Mui-disabled, .Mui-error):before': {
            borderBottom: `2px solid ${theme.vars.palette.grey[400]}`,
          },
          '&.Mui-focused': {
            '&:before': {
              borderBottom: `2px solid ${theme.vars.palette.grey[400]}`,
            },
            '&:after': {
              borderBottom: 'none',
            },
          },
          '&.Mui-error': {
            borderBottomColor: theme.vars.palette.error.main,
            '&:before': {
              borderBottom: `2px solid ${theme.vars.palette.error.main}`,
            },
            '&:after': {
              borderBottom: 'none',
            },
          },
        },
        '& .MuiInput-input': {
          fontSize: '16px',
          fontFamily: '"New Hero", sans-serif',
          lineHeight: 1.7,
          '&::placeholder': {
            color: theme.vars.palette.text.disabled,
            opacity: 1,
            fontFamily: '"New Hero", sans-serif',
            fontSize: '16px',
            lineHeight: 1.7,
          },
        },
        '& .MuiFormHelperText-root': {
          fontSize: '14px',
          fontFamily: '"New Hero", sans-serif',
          lineHeight: 1.6,
          color: theme.vars.palette.text.secondary,
          marginTop: '4px',
          '&.Mui-error': {
            color: theme.vars.palette.error.main,
          },
        },
      }),
    },
  ],
};

export const textfield = {
  MuiInput,
  MuiInputBase,
  MuiFilledInput,
  MuiOutlinedInput,
  MuiTextField,
  MuiInputLabel,
};
